import { DocumentTextIcon } from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { Header, PageWrapper } from "src/shared/components";
import { SPONSOR_POST_EVENT_REPORT_RELEASED } from "src/shared/constants/featureFlags";
import { useSponsorContext } from "src/shared/contexts/SponsorContext";

import { useGetSponsorPostEventReportQuery } from "./graphql/getSponsorPostEventReport.generated";
import PostEventReportReader from "./PostEventReportReader";
import {
  getReportDownloadFilename,
  getReportRefreshDelayMs,
  REPORT_TITLE,
} from "./reportAccess";

const PostEventReport: React.FC = () => {
  const { company, loading: sponsorLoading } = useSponsorContext();

  // The server resolves the caller's company itself and returns `null` for
  // anyone not entitled to a report; the slug only matters for organizers,
  // who have no sponsor record to be pinned to.
  const {
    data,
    loading: reportLoading,
    error,
    refetch,
  } = useGetSponsorPostEventReportQuery({
    variables: { sponsor_company_slug: company?.slug },
    skip: !SPONSOR_POST_EVENT_REPORT_RELEASED || sponsorLoading,
  });

  const report = data?.sponsorPostEventReport ?? undefined;
  const reportExpiry = report?.expiry;
  const companyName = company?.name ?? "Your company";
  const loading = sponsorLoading || reportLoading;

  // Re-issue the signed URL shortly before it lapses so a tab left open over
  // the weekend does not start rendering from a dead link.
  useEffect(() => {
    const delay = getReportRefreshDelayMs(reportExpiry);
    if (delay === null) return undefined;

    const timer = window.setTimeout(() => void refetch(), delay);
    return () => window.clearTimeout(timer);
  }, [reportExpiry, refetch]);

  // One automatic recovery per visit: enough to survive an expired URL,
  // bounded so a genuinely broken link cannot loop.
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  const handleStaleReportUrl = useCallback(() => {
    if (recoveryAttempted) return false;
    setRecoveryAttempted(true);
    void refetch();
    return true;
  }, [recoveryAttempted, refetch]);

  const content = (() => {
    if (!SPONSOR_POST_EVENT_REPORT_RELEASED) {
      return (
        <ReportState>
          <StateIcon aria-hidden="true">
            <DocumentTextIcon />
          </StateIcon>
          <StateEyebrow>Post-event report</StateEyebrow>
          <StateTitle>Your report is coming soon.</StateTitle>
          <StateCopy>
            We&apos;re putting the finishing touches on your sponsorship recap.
            You&apos;ll be able to read and download it here after the event.
          </StateCopy>
        </ReportState>
      );
    }

    if (loading) {
      return (
        <ReportState aria-live="polite">
          <StateIcon aria-hidden="true">
            <DocumentTextIcon />
          </StateIcon>
          <StateTitle>Finding your report...</StateTitle>
          <StateCopy>This should only take a moment.</StateCopy>
        </ReportState>
      );
    }

    if (error) {
      return (
        <ReportState role="alert">
          <StateIcon aria-hidden="true">
            <DocumentTextIcon />
          </StateIcon>
          <StateEyebrow>Post-event report</StateEyebrow>
          <StateTitle>We couldn&apos;t load your report.</StateTitle>
          <StateCopy>
            Something went wrong on our end. Refresh the page to try again, or
            contact{" "}
            <ContactLink href="mailto:sponsor@hackthenorth.com">
              sponsor@hackthenorth.com
            </ContactLink>{" "}
            if it keeps happening.
          </StateCopy>
        </ReportState>
      );
    }

    if (!report) {
      return (
        <ReportState>
          <StateIcon aria-hidden="true">
            <DocumentTextIcon />
          </StateIcon>
          <StateEyebrow>Post-event report</StateEyebrow>
          <StateTitle>Your report is still being prepared.</StateTitle>
          <StateCopy>
            We&apos;ll let you know as soon as it&apos;s ready. If you expected
            to see it already, contact{" "}
            <ContactLink href="mailto:sponsor@hackthenorth.com">
              sponsor@hackthenorth.com
            </ContactLink>
            .
          </StateCopy>
        </ReportState>
      );
    }

    return (
      // Remount on a new signed URL so the reader restarts cleanly after a
      // refresh rather than holding stale load state.
      <PostEventReportReader
        key={report.uri}
        uri={report.uri}
        title={REPORT_TITLE}
        downloadFilename={getReportDownloadFilename(report.name)}
        onStale={handleStaleReportUrl}
      />
    );
  })();

  return (
    <>
      <Header
        title={REPORT_TITLE}
        subtitle={`${companyName}'s Hack the North sponsorship recap`}
      />
      <ReportPageWrapper fullWidth>{content}</ReportPageWrapper>
    </>
  );
};

const ReportPageWrapper = styled(PageWrapper)`
  padding: 40px 48px 64px;
  background: ${({ theme }) => theme.globalConstants.color.offWhite};

  > div {
    max-width: 1024px;
    margin: 0 auto;
  }

  ${({ theme }) => theme.mediaQueries.tablet`
    padding: 28px 24px 48px;
  `}

  ${({ theme }) => theme.mediaQueries.largeMobile`
    padding: 20px 0 40px;
  `}
`;

const ReportState = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100%, 620px);
  margin: 48px auto;
  padding: 56px 48px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 18px;
  background: white;
  text-align: center;
  box-shadow: 0 12px 36px rgba(14, 52, 96, 0.07);

  ${({ theme }) => theme.mediaQueries.largeMobile`
    width: calc(100% - 32px);
    margin: 24px auto;
    padding: 40px 24px;
  `}
`;

const StateIcon = styled.div`
  display: grid;
  width: 58px;
  height: 58px;
  margin-bottom: 22px;
  place-items: center;
  border-radius: 16px;
  color: ${({ theme }) => theme.globalConstants.color.navySecondary};
  background: ${({ theme }) => theme.globalConstants.color.bluePrimary3Light};

  svg {
    width: 29px;
    height: 29px;
  }
`;

const StateEyebrow = styled.p`
  margin: 0 0 10px;
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
`;

const StateTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textDark};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 32px;
  line-height: 1.2;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    font-size: 27px;
  `}
`;

const StateCopy = styled.p`
  max-width: 470px;
  margin: 16px 0 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  line-height: 1.65;
`;

const ContactLink = styled.a`
  color: ${({ theme }) => theme.globalConstants.color.navySecondary};
  font-weight: 700;
`;

export default PostEventReport;
